import { useRef } from "react";
import { Box, Stack, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid-premium";
import { DynamicShared } from "../../../dynamicShared";
import { Translate, useTranslation } from "@privacy/translations";
import type { TranslationKey } from "@privacy/translations";
import { useMutation } from "@tanstack/react-query";
import {
  archiveSystemMutationOptions,
  useGetSystemById,
  type SystemConnectionKind,
  type SystemConnectionRow,
} from "../../../api/systems";
import type { SystemById } from "../../../api/systems.types";
import { ReplaceAndArchiveDialog } from "../../../shared/ReplaceAndArchiveDialog";
import { ConfirmDeleteDialog } from "../../../shared/ConfirmDeleteDialog";
import { Drawer } from "../../../shared/Drawer";
import { InformationTypeIcon2, RegistrationControllerIcon1, RegistrationProcessorIcon1 } from "../../../shared/icons";
import { RepointConnectionsDialog } from "./RepointConnectionsDialog";

export const SystemReplaceAndArchiveDialog = ({
  systemId,
  systemName,
  onClose,
}: {
  systemId: string;
  systemName: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const systemQuery = useGetSystemById(systemId);
  const archiveMutation = useMutation(archiveSystemMutationOptions(systemId));

  const connections = systemQuery.data ? toSystemConnectionRows(systemQuery.data) : [];
  const totalConnectionCount = systemQuery.data ? getSystemConnectionTotalCount(systemQuery.data) : 0;

  /** Whether the system had connections when first opened — decides confirm vs the repoint grid.
   *  Captured once so repointing down to zero keeps the grid (with an enabled Archive button)
   *  instead of flipping to the simple confirm. */
  const startedWithConnectionsRef = useRef<boolean | null>(null);
  if (startedWithConnectionsRef.current === null && systemQuery.isSuccess && !systemQuery.isFetching) {
    startedWithConnectionsRef.current = totalConnectionCount > 0;
  }

  if (startedWithConnectionsRef.current === null) {
    return null;
  }

  // Systems with no connections at open archive via a simple confirm (mirrors the IT case).
  if (startedWithConnectionsRef.current === false) {
    return (
      <ConfirmDeleteDialog
        isOpen
        onClose={onClose}
        onDelete={() => archiveMutation.mutateAsync()}
        headerText={t("System.Archive.Confirm.Title")}
        message={
          <Translate
            translationKey="System.Archive.Confirm.Message"
            values={{
              name: (
                <Box component="span" sx={{ fontWeight: "bold" }}>
                  {systemName}
                </Box>
              ),
            }}
          />
        }
        confirmButtonLabel={t("System.Archive.Confirm.Button")}
        id="confirm-archive-system"
        onKeyDown={e => e.key === "Tab" && e.stopPropagation()}
      />
    );
  }

  return (
    <ReplaceAndArchiveDialog<SystemConnectionRow>
      onClose={onClose}
      isLoading={false}
      title={t("System.ReplaceAndArchive.Dialog.Title")}
      subtitle={
        totalConnectionCount > 0
          ? t("System.ReplaceAndArchive.Dialog.Subtitle", {
              name: systemName,
              count: totalConnectionCount,
              connectionLabel: t(
                totalConnectionCount === 1
                  ? "System.ReplaceAndArchive.Dialog.ConnectionLabel.Singular"
                  : "System.ReplaceAndArchive.Dialog.ConnectionLabel.Plural"
              ),
            })
          : t("System.ReplaceAndArchive.Dialog.ReadyToArchive")
      }
      rows={connections}
      getRowId={row => row.id}
      columns={buildColumns(t)}
      searchPlaceholder={t("Drawer.SubDrawer.GridSearchPlaceholder")}
      replaceSelected={{
        label: t("System.ReplaceAndArchive.Dialog.ReplaceSelected"),
        renderDialog: ({ selectedRows, onClose: onReplaceClose }) => (
          <RepointConnectionsDialog
            systemId={systemId}
            systemName={systemName}
            selectedConnections={selectedRows}
            onClose={onReplaceClose}
          />
        ),
      }}
      archive={{
        label: t("System.Archive.Label"),
        isDisabled: totalConnectionCount > 0,
        isPending: archiveMutation.isPending,
        onArchive: () => archiveMutation.mutate(undefined, { onSuccess: () => onClose() }),
      }}
      emptyState={<Drawer.GridNoRowsOverlay labelKey="System.ReplaceAndArchive.Dialog.NoConnections" />}
    />
  );
};

const KindIcon = ({ kind }: { kind: SystemConnectionKind }) => {
  switch (kind) {
    case "controllerRegistration":
      return <RegistrationControllerIcon1 size="sm" />;
    case "processorRegistration":
      return <RegistrationProcessorIcon1 size="sm" />;
    default:
      return <InformationTypeIcon2 size="sm" />;
  }
};

const buildColumns = (t: (key: TranslationKey) => string): Array<GridColDef<SystemConnectionRow>> => [
  {
    field: "name",
    width: 400,
    headerName: t("System.ReplaceAndArchive.Dialog.NameHeader"),
    renderCell: ({ row }) => (
      <Drawer.GridCell>
        <Stack direction="row" sx={{ gap: 2, alignItems: "center" }}>
          <KindIcon kind={row.kind} />
          <Typography sx={{ fontWeight: 600, lineHeight: 1.2, fontSize: theme => theme.typography.pxToRem(14) }}>
            {row.name}
          </Typography>
        </Stack>
      </Drawer.GridCell>
    ),
  },
  {
    field: "connectionType",
    width: 180,
    sortable: false,
    headerName: t("System.ReplaceAndArchive.Dialog.TypeHeader"),
    renderCell: ({ row }) => (
      <Drawer.GridCell>
        <DynamicShared.Chip variant="choice" label={t(CONNECTION_TYPE_LABELS[row.kind])} />
      </Drawer.GridCell>
    ),
  },
  {
    field: "subType",
    width: 150,
    sortable: false,
    headerName: t("System.ReplaceAndArchive.Dialog.RoleHeader"),
    renderCell: ({ row }) => {
      const subType = SUB_TYPE_LABELS[row.kind];
      return (
        <Drawer.GridCell>{subType ? <DynamicShared.Chip variant="choice" label={t(subType)} /> : null}</Drawer.GridCell>
      );
    },
  },
];

const toSystemConnectionRows = (system: SystemById): SystemConnectionRow[] => [
  ...system.connectedControllerRegistrations.registrations.map(({ id, name }): SystemConnectionRow => ({
    id,
    name,
    kind: "controllerRegistration",
  })),
  ...system.connectedProcessorRegistrations.registrations.map(({ id, name }): SystemConnectionRow => ({
    id,
    name,
    kind: "processorRegistration",
  })),
  ...system.connectedInformationTypesInfo.storedConnections.map(({ connectionId, name }): SystemConnectionRow => ({
    id: connectionId,
    name,
    kind: "informationTypeStored",
  })),
  ...system.connectedInformationTypesInfo.usedConnections.map(({ connectionId, name }): SystemConnectionRow => ({
    id: connectionId,
    name,
    kind: "informationTypeUsed",
  })),
];

const getSystemConnectionTotalCount = (system: SystemById): number =>
  system.connectedControllerRegistrations.totalCount +
  system.connectedProcessorRegistrations.totalCount +
  system.connectedInformationTypesInfo.storedConnections.length +
  system.connectedInformationTypesInfo.usedConnections.length;

const CONNECTION_TYPE_LABELS: Record<SystemConnectionKind, TranslationKey> = {
  controllerRegistration: "System.ReplaceAndArchive.ConnectionType.Registration",
  processorRegistration: "System.ReplaceAndArchive.ConnectionType.Registration",
  informationTypeStored: "System.ReplaceAndArchive.ConnectionType.InformationType",
  informationTypeUsed: "System.ReplaceAndArchive.ConnectionType.InformationType",
};

/** null ⇒ no sub type (registrations have none now that controller/processor isn't shown). */
const SUB_TYPE_LABELS: Record<SystemConnectionKind, TranslationKey | null> = {
  controllerRegistration: null,
  processorRegistration: null,
  informationTypeStored: "System.ReplaceAndArchive.SubType.Stored",
  informationTypeUsed: "System.ReplaceAndArchive.SubType.Used",
};
